<template>
  <div class="care-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="care-container">
      <!-- Header Section -->
      <div class="care-header">
        <div class="header-content">
          <div class="back-section">
            <v-btn class="btn-return" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>
          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-heart title-icon"></i>
              Cuidados
            </h1>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="care-content">
        <!-- Resident Info Card -->
        <div class="resident-card">
          <b-overlay
            :show="loadingResident"
            opacity="0.99"
            class="loading-overlay"
          >
            <div class="resident-info">
              <div class="resident-avatar">
                <i class="fas fa-user-circle"></i>
              </div>
              <div class="resident-details">
                <h3 class="resident-name">{{ resident.name }}</h3>
                <span class="resident-label">Residente</span>
              </div>
            </div>
          </b-overlay>
        </div>

        <!-- Filters Card -->
        <div class="filters-card">
          <div class="card-header">
            <h3 class="card-title">
              <i class="fas fa-filter mr-2"></i>
              Filtros
            </h3>
          </div>
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">Usuário</label>
              <v-select
                :items="typesFilterByUser"
                v-model="filterByUser"
                outlined
                dense
                hide-details
                class="modern-select"
                @change="getCaresResident"
              />
            </div>

            <div class="filter-group">
              <label class="filter-label">Status</label>
              <v-select
                :items="typesFilterByStatus"
                v-model="status"
                outlined
                dense
                hide-details
                class="modern-select"
                @change="getCaresResident"
              />
            </div>

            <div class="filter-group">
              <label class="filter-label">Data Inicial</label>
              <v-text-field
                type="date"
                v-model="dtInit"
                outlined
                dense
                hide-details
                class="modern-input"
                @input="getCaresResident"
              />
            </div>

            <div class="filter-group">
              <label class="filter-label">Data Final</label>
              <v-text-field
                type="date"
                v-model="dtEnd"
                outlined
                dense
                hide-details
                class="modern-input"
                @input="getCaresResident"
              />
            </div>
          </div>
        </div>

        <!-- Data Table Card -->
        <div class="table-card">
          <div class="card-header">
            <h3 class="card-title">
              <i class="fas fa-list mr-2"></i>
              Lista de Cuidados
            </h3>
            <div class="search-section">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Pesquisar cuidados..."
                outlined
                dense
                hide-details
                class="search-input"
              />
            </div>
          </div>

          <div class="table-content">
            <b-overlay
              :show="loadingCares"
              opacity="0.99"
              class="loading-overlay"
            >
              <v-data-table
                dense
                :headers="headers"
                :items="cares"
                :search="search"
                item-key="id"
                class="modern-table"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                @click:row="handleClickRowTable"
              >
                <template v-slot:[`item.type`]="{ item }">
                  <span class="type-badge">{{ handleType(item.type) }}</span>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <span
                    v-if="item.typeItem == 'CARE'"
                    :class="[
                      'status-badge',
                      item.status == 'A' ? 'status-warning' : 'status-success',
                    ]"
                  >
                    <i
                      :class="
                        item.status == 'A' ? 'fas fa-clock' : 'fas fa-check'
                      "
                      class="mr-1"
                    ></i>
                    {{ item.status == "A" ? "Em Aberto" : "Finalizado" }}
                  </span>
                  <span
                    v-if="item.typeItem == 'VITAL_SIGN'"
                    :class="[
                      'status-badge',
                      item.pending_vital_signs > 0
                        ? 'status-warning'
                        : 'status-success',
                    ]"
                  >
                    <i
                      :class="
                        item.pending_vital_signs > 0
                          ? 'fas fa-clock'
                          : 'fas fa-check'
                      "
                      class="mr-1"
                    ></i>
                    {{
                      item.pending_vital_signs > 0 ? "Em Aberto" : "Finalizado"
                    }}
                  </span>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                  <span class="date-text">{{
                    formatDate(item.created_at)
                  }}</span>
                </template>

                <template v-slot:[`item.typeItem`]="{ item }">
                  <span
                    class="item-type-badge"
                    :class="
                      item.typeItem == 'CARE' ? 'type-care' : 'type-vital'
                    "
                  >
                    <i
                      :class="
                        item.typeItem == 'CARE'
                          ? 'fas fa-edit'
                          : 'fas fa-heartbeat'
                      "
                      class="mr-1"
                    ></i>
                    {{ item.typeItem == "CARE" ? "Anotação" : "Sinais Vitais" }}
                  </span>
                </template>

                <template v-slot:[`item.pending_vital_signs`]="{ item }">
                  <div class="pending-section">
                    <div
                      v-if="item.status == 'A'"
                      class="pending-indicator care-pending"
                    >
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>Pendente</span>
                    </div>
                    <div
                      v-if="
                        item.typeItem == 'VITAL_SIGN' &&
                        item.pending_vital_signs > 0
                      "
                      class="pending-indicator vital-pending"
                    >
                      <span class="pending-count">{{
                        item.pending_vital_signs
                      }}</span>
                      <span>Pendente</span>
                    </div>
                  </div>
                </template>
              </v-data-table>
            </b-overlay>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../../templates/NavBar";
import AuthBySessionExpired from "../../templates/Components/AuthBySessionExpired";
import api from "../../services/api";
import moment from "moment";
import { formatDate } from "../../lib/format";
import routes from "../../routes";
import { userData } from "../../lib/functions";

export default {
  name: "UserCare",
  components: {
    NavBar,
    AuthBySessionExpired,
  },
  data() {
    return {
      search: "",
      popupTypeCreate: false,
      headers: [
        { text: "TIPO", sortable: false, value: "type" },
        { text: "CRIADO POR", value: "user_name" },
        { text: "STATUS", value: "status" },
        { text: "DATA CADASTRO", value: "created_at" },
        { text: "CATEGORIA", value: "typeItem" },
        { text: "PENDÊNCIAS", value: "pending_vital_signs" },
      ],
      typesFilterByUser: [
        { value: "ALL", text: "Todos os usuários" },
        { value: "MY", text: "Apenas meu usuário" },
      ],
      typesFilterByStatus: [
        { value: "TODOS", text: "Todos os status" },
        { value: "ABERTO", text: "Apenas em aberto" },
        { value: "FINALIZADO", text: "Apenas finalizados" },
      ],
      rest_home_id: "",
      resident_id: "",
      user_id: "",
      status: "ABERTO",
      filterByUser: "USER",
      resident: {},
      user: {},
      user_name: "",
      cares: [],
      loadingResident: true,
      loadingCares: true,
      dtInit: "",
      dtEnd: "",
      loading: true,
      errors: [],
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    formatDate,
    moment,
    handleClickRowTable({ id, typeItem }) {
      if (typeItem == "CARE") {
        routes.push({ name: "care", params: { id } });
      }
      if (typeItem == "VITAL_SIGN") {
        routes.push({ name: "vital_sign", params: { id } });
      }
    },
    handleType(type) {
      var show = {
        CUIDADOR_ENFERMAGEM: "Cuidador/Enfermagem",
        ENFERMEIRA: "Enfermeira",
        MEDICO: "Médico",
        EQUIPE_MULTIDISCIPLINAR: "Equipe Multidisciplinar",
        default: "Não encontrado",
      };
      return show[type] || show.default;
    },
    async retrieve() {
      const { user } = userData();
      this.rest_home_id = user.rest_home_id;
      this.user = user;
      const { user_id, resident_id } = routes.history.current.params;
      this.user_id = user_id;
      this.resident_id = resident_id;
      await this.getUser();
      await this.getResident();
      await this.getCaresResident();
    },
    async getUser() {
      try {
        const { user_id } = this;
        const response = await api.get("/rest-home/user/get", {
          params: { id: user_id },
        });
        const { user, error } = response.data;
        if (error) {
          return routes.push({ name: "cares2" });
        }
        this.typesFilterByUser.push({
          value: "USER",
          text: `Apenas ${user.name}`,
        });
      } catch (error) {
        console.log(error);
      }
    },
    async getResident() {
      try {
        this.loadingResident = true;
        const { resident_id, rest_home_id } = this;
        const response = await api.get("/rest-home/resident/path", {
          params: { id: resident_id, rest_home_id },
        });
        const { resident, error } = response.data;
        if (error) {
          return routes.push({ name: "cares2" });
        }
        this.resident = resident;
        this.loadingResident = false;
      } catch (error) {
        console.log(error);
      }
    },
    async getCaresResident() {
      try {
        this.loadingCares = true;
        const {
          resident_id,
          rest_home_id,
          user,
          user_id,
          dtInit,
          dtEnd,
          status,
          filterByUser,
        } = this;
        var filterByUserId = user_id;
        if (filterByUser == "MY") {
          filterByUserId = user.id;
        }
        var params = {
          resident_id,
          rest_home_id,
          user_id: filterByUserId,
          dtInit,
          dtEnd,
          status,
          filterByUser,
        };
        const response = await api.get("/rest-home/care/user/filter", {
          params,
        });
        const { cares, error } = response.data;
        if (error) {
          return routes.push({ name: "dashboard" });
        }
        this.cares = cares;
        this.loadingCares = false;
      } catch (error) {
        console.log(error);
      }
    },
    handleToBack: () => routes.go(-1),
  },
};
</script>

<style scoped>
.care-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.care-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header */
.care-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

/* Content */
.care-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Resident Card */
.resident-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.resident-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.resident-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.resident-name {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.resident-label {
  font-size: 14px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Filters Card */
.filters-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.filters-grid {
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.search-section {
  flex: 1;
  max-width: 300px;
}

.search-input {
  border-radius: 12px;
}

.table-content {
  padding: 0;
}

/* Table Styling */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 0 16px 16px;
}

.modern-table >>> thead tr th {
  background: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> tbody tr {
  cursor: pointer;
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background: #f8fafc !important;
  transform: translateY(-1px);
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
  border: 1px solid #f59e0b;
}

.type-badge {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.item-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-care {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
}

.type-vital {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
  color: #be185d;
}

.date-text {
  font-size: 13px;
  color: #6b7280;
}

/* Pending Section */
.pending-section {
  display: flex;
  justify-content: flex-end;
}

.pending-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.care-pending {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
}

.vital-pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
}

.pending-count {
  background: rgba(255, 255, 255, 0.8);
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
}

/* Loading Overlays */
.loading-overlay {
  border-radius: 16px;
}

/* Custom Vuetify Overrides */
.modern-select >>> .v-input__control,
.modern-input >>> .v-input__control {
  border-radius: 12px !important;
}

.modern-select >>> .v-select__selection,
.modern-input >>> .v-text-field__slot input {
  font-weight: 500 !important;
  font-size: 14px !important;
}

/* Responsive */
@media (max-width: 768px) {
  .care-container {
    padding: 0 16px;
  }

  .care-header {
    padding: 16px 0;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .page-title {
    font-size: 24px;
    justify-content: center;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .resident-info {
    text-align: center;
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .resident-avatar {
    width: 48px;
    height: 48px;
    font-size: 18px;
  }

  .resident-name {
    font-size: 20px;
  }

  .filters-card,
  .table-card {
    border-radius: 12px;
  }
}
</style>